import { useCallback, useEffect, useId, useRef } from 'react';
import type { z } from 'zod';
import { useSetAtom } from 'jotai';

import { useLogger } from '@archipro-website/logger/client';
import { useTracker } from '@archipro-website/tracker';
import {
    assertErrorResponse,
    assertSuccessResponse,
} from '@modules/root/graphql/responses';
import { useUserLoggedIn } from '@modules/user';
import { trackSubmitCreateEnquiryRequest } from '@modules/tracking/util/trackSubmitCreateEnquiryRequest';
import { trackBrochureRequest } from '@modules/tracking/util/trackBrochureRequest';
import { trackProjectEnquire } from '@modules/tracking/util/trackProjectEnquiry';
import { fileStorage } from '~/utils/file-storage';
import {
    useBulkEnquiryFanOut,
    useResetBulkEnquiry,
} from '../bulk/bulk-enquiry-state';
import { enquiryFormSessionSchema } from '../validation';
import { useEnquiryEvent, useEnquirySession } from './use-enquiry';
import { useIsSocialLoginCallback } from '~/modules/root/hook/use-is-social-logic-callback';
import { trackCreatedEnquiry } from '~/modules/tracking/util/trackCreatedEnquiry';
import { enquiryModalOpenAtom } from './use-enquiry-modal';

/**
 * Hook to enable enquiry form auto-submission after login. This hook should
 * only be called once in the component tree near the root component.
 * @see {@link packages/web/app/modules/enquiry/hooks/use-enquiry.ts}
 */
export function useEnquiryAutoSubmit() {
    const id = useId();
    const { isLoggedIn, user } = useUserLoggedIn();
    const logger = useLogger();
    const tracker = useTracker();
    const isSocialLoginCallback = useIsSocialLoginCallback(true);
    const setEnquiryModalOpen = useSetAtom(enquiryModalOpenAtom);
    const resetBulkEnquiry = useResetBulkEnquiry();
    const fanOut = useBulkEnquiryFanOut();
    /**
     * The fan-out of the submission actually sent, captured as it goes.
     *
     * `fanOut` is derived from the live selection, which the success path
     * empties; and a handler built before the selection landed closes over an
     * empty one. Reading either at report time is how a fifty-supplier bulk
     * send came to be reported as reaching nobody. Mirrors `sentFanOutRef` in
     * {@link ./use-enquiry.ts}.
     */
    const sentFanOutRef = useRef<typeof fanOut | undefined>(undefined);
    // Form calls this to register latest file attachments
    const filesRef = useRef<File[]>([]);
    const {
        fetcher,
        enquirySession,
        enquiryFormId,
        enquiryAutoSubmit,
        autoSubmitDelay,
        isAutoSubmitted,
        setEnquirySession,
        setEnquiryFormId,
        setIsAutoSubmitted,
        setEnquiryAutoSubmit,
    } = useEnquirySession();

    const onSuccess = useCallback(() => {
        if (!isAutoSubmitted) {
            return;
        }
        // A bulk enquiry that lands here was sent from the saved session after
        // login, so the browse page's modal never got to run its success path.
        // Empty the list and leave bulk mode, or the island comes back offering a
        // selection that has already been sent — and sending it again duplicates
        // every enquiry in it.
        const wasBulk = Boolean(enquirySession?.siteTreeIds?.length);
        trackCreatedEnquiry(tracker, {
            source: enquirySession?.source ?? 'InPage',
            professionalId: enquirySession?.professional?.ID,
            fanOut: sentFanOutRef.current,
        });
        if (wasBulk) resetBulkEnquiry();
        // The user is already logged in at this stage, don't need to track
        // the auth event.
        setEnquirySession((session) => ({
            ...session,
            message: '',
            ...(wasBulk ? { siteTreeIds: undefined } : {}),
        }));
        setEnquiryAutoSubmit(false);
    }, [
        enquirySession?.source,
        enquirySession?.siteTreeIds,
        enquirySession?.professional?.ID,
        isAutoSubmitted,
        resetBulkEnquiry,
        setEnquiryAutoSubmit,
        setEnquirySession,
        tracker,
    ]);
    const { triggerSuccess, triggerFailure } = useEnquiryEvent({ onSuccess });

    /**
     * Similar to `submitEnquiry` in `useEnquiry`.
     * @see {@link ./use-enquiry.ts}
     */
    const autoSubmitEnquiry = useCallback(
        (values: z.infer<typeof enquiryFormSessionSchema>) => {
            // Prevent duplicate submissions if already submitting.
            if (fetcher.state !== 'idle') {
                return;
            }
            if (values.isModal) {
                // If the in-page form was submitted, use the in-page form
                // post-submission logic to show the success animation.
                // Otherwise, use the auto-submit post-submission logic.
                setEnquiryFormId(id);
            }
            setIsAutoSubmitted(true);
            // The item count comes from the session being resent — the selection
            // atom may not have rehydrated yet after a social round-trip — and
            // only the supplier count needs the live list.
            sentFanOutRef.current = values.siteTreeIds?.length
                ? {
                      itemCount: values.siteTreeIds.length,
                      supplierCount: fanOut.supplierCount,
                  }
                : undefined;
            // Fall back to the member's profile phone; the backend requires a
            // phone to share contact details, so only share when we have one.
            const phoneNumber =
                values.phoneNumber || (isLoggedIn ? (user.Phone ?? '') : '');
            const payload = {
                name: values.name,
                email: isLoggedIn && user.Email ? user.Email : values.email,
                phoneNumber,
                suburbPostcode: values.postcode?.value?.region ?? '',
                // See use-enquiry: stringifying an absent postcode yields the
                // undefined value, which posts as the literal "undefined".
                locationResponse: values.postcode?.value
                    ? JSON.stringify(values.postcode.value)
                    : '',
                branchId: values.branchId ?? null,
                message: values.message,
                siteTreeId: values.siteTreeId,
                targetProfessionalId: values.professional.ID,
                // Re-send the full bulk selection when present (set only for
                // bulk enquiries); otherwise the single ids above are used.
                siteTreeIds: values.siteTreeIds
                    ? JSON.stringify(values.siteTreeIds)
                    : undefined,
                // The server rejects sharing contact details without a phone.
                shareDetails: phoneNumber ? 'true' : 'false',
            };
            const formData = new FormData();
            for (const [key, value] of Object.entries(payload)) {
                if (value !== undefined && value !== null) {
                    formData.append(key, String(value));
                }
            }

            // Add files to FormData if they exist
            if (filesRef.current.length > 0) {
                filesRef.current.forEach((file) => {
                    formData.append('Attachments[]', file);
                });
            }

            fetcher.submit(formData, {
                method: 'POST',
                action: '/remix-api/enquiry/submit',
                encType: 'multipart/form-data',
            });

            // Left as it was: EnquirySubmit never reaches the warehouse (it is
            // on the ArchiPro pipeline's ignore list), so the fan-out goes on
            // the submit-attempt step below, which does.
            tracker.log('EnquirySubmit', {
                url: new URL(window.location.href),
                label: values.professional.Title,
            });
            trackSubmitCreateEnquiryRequest(tracker, {
                source: values.source,
                isAutoSubmit: true,
                fanOut: sentFanOutRef.current,
            });
            if (
                values.itemType === 'professional' ||
                values.itemType === 'product'
            ) {
                trackBrochureRequest(tracker, {
                    type: values.itemType,
                    region: payload.suburbPostcode,
                });
            } else if (values.itemType === 'project' && values.relatedItem) {
                trackProjectEnquire(tracker, {
                    category: values.relatedItem.category,
                    step: 2,
                    project: values.relatedItem.title,
                    professional: values.professional.Title,
                    region: payload.suburbPostcode,
                });
            }
            if (values.source === 'InPage') {
                tracker.log('OnPageEnquirySend', {
                    url: new URL(window.location.href),
                    targetTracker: 'archiproTracker',
                });
            }
        },
        [
            id,
            isLoggedIn,
            user,
            fetcher,
            fanOut,
            tracker,
            setEnquiryFormId,
            setIsAutoSubmitted,
        ],
    );

    // Automatically submit enquiry from saved session after login.
    useEffect(() => {
        if (
            !enquiryAutoSubmit ||
            !enquirySession ||
            !isLoggedIn ||
            autoSubmitDelay ||
            isSocialLoginCallback ||
            fetcher.state !== 'idle'
        ) {
            return;
        }

        // Clear the auto-submit flag immediately to prevent race conditions
        // where this effect could re-trigger during async operations.
        setEnquiryAutoSubmit(false);

        async function tryAutoSubmit() {
            const result = enquiryFormSessionSchema.safeParse(enquirySession);
            if (!result.success) {
                const flat = result.error.flatten();
                console.warn(
                    'Enquiry auto-submit session validation failed',
                    flat,
                );
                logger.error('Enquiry auto-submit session validation failed', {
                    error: flat,
                });
                // The user did everything right and the system dropped their
                // enquiry — without this event the session reads as voluntary
                // post-login abandonment in funnel analysis.
                tracker.log('EnquirySubmitError', {
                    url: new URL(window.location.href),
                    data: {
                        reason: 'session_restore_failed',
                        isAutoSubmit: true,
                        Source: enquirySession?.source ?? 'InPage',
                        ExtraData: JSON.stringify({
                            reason: 'session_restore_failed',
                            isAutoSubmit: true,
                        }),
                    },
                });
                // The gate is holding a "Sending your enquiry…" step waiting on
                // this, and blocks its own dismissal while it does. Returning
                // silently left the reader in a modal they could not close,
                // watching a send that was never going to happen.
                triggerFailure();
                return;
            }

            const sessionId = `${result.data.professional.ID}-${result.data.siteTreeId}`;
            const files = await fileStorage.getFiles(sessionId);
            filesRef.current = files;
            // autoSubmitEnquiry sets isAutoSubmitted synchronously,
            // avoiding a render gap where the loading overlay would disappear.
            autoSubmitEnquiry(result.data);
        }

        // A bulk enquiry has its own modal on the browse page. This one is the
        // single-item enquiry modal, and it is given none of the bulk context, so
        // opening it here would front an unrelated (and empty) enquiry form.
        if (enquirySession.isModal && !enquirySession.siteTreeIds?.length) {
            setEnquiryModalOpen(true);
        }

        void tryAutoSubmit();
    }, [
        isLoggedIn,
        enquirySession,
        enquiryAutoSubmit,
        autoSubmitDelay,
        isSocialLoginCallback,
        fetcher.state,
        autoSubmitEnquiry,
        setEnquiryAutoSubmit,
        setEnquiryModalOpen,
        logger,
        tracker,
        triggerFailure,
    ]);

    // Similar to the `useEffect` in `useEnquiry`.
    const isHandleSubmitComplete = useRef(true);
    useEffect(() => {
        if (enquiryFormId !== id) {
            // Only run if the form was auto-submitted.
            return;
        } else if (fetcher.state === 'submitting') {
            // Ensure this is only run once per submission.
            isHandleSubmitComplete.current = false;
            return;
        } else if (isHandleSubmitComplete.current || !fetcher.data) {
            return;
        }
        // Reports as soon as the action answers, not once the route
        // revalidation behind it finishes — see the matching note in
        // `useEnquiry`. This is the resume path, where that wait sat at the end
        // of the longest journey in the flow.
        const isSuccess = assertSuccessResponse(fetcher.data);
        const isError = assertErrorResponse(fetcher.data);
        if (isSuccess) {
            triggerSuccess();
        } else if (isError) {
            setIsAutoSubmitted(false);
            // The reader did everything right and their enquiry did not land.
            // Nothing else tells them, so announce it rather than leaving the
            // failure to the logs.
            triggerFailure();
            logger.error('Enquiry auto-submit failed', {
                error: fetcher.data,
            });
            // System failure, not user drop-off — see session_restore_failed.
            tracker.log('EnquirySubmitError', {
                url: new URL(window.location.href),
                data: {
                    reason: 'auto_submit_failed',
                    isAutoSubmit: true,
                    Source: enquirySession?.source ?? 'InPage',
                    ExtraData: JSON.stringify({
                        reason: 'auto_submit_failed',
                        isAutoSubmit: true,
                    }),
                },
            });
        }
        isHandleSubmitComplete.current = true;
    }, [
        id,
        fetcher,
        logger,
        tracker,
        enquirySession,
        enquiryFormId,
        setEnquirySession,
        setEnquiryAutoSubmit,
        triggerSuccess,
        triggerFailure,
        setIsAutoSubmitted,
    ]);
}
